/**
 * CommandPalette — global ⌘K / Ctrl+K search + navigation.
 *
 * Searches the loaded client stores (projects, portfolios, programs, and the
 * risks / change requests / resources nested in them) plus static report and
 * navigation destinations. Results are grouped by type, keyboard-navigable
 * (↑/↓ to move, Enter to open, Esc to close). For a PMO director with dozens of
 * projects this turns every jump into one keystroke plus a few characters.
 *
 * Everything here is client-side over already-loaded data — the whole portfolio
 * (with its nested registers) lives in useProjectStore after load, so no extra
 * fetch is needed to reach any of it.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  FolderKanban,
  Target,
  Boxes,
  AlertTriangle,
  GitBranch,
  Users,
  BarChart3,
  ArrowRight,
  LayoutDashboard,
  Calendar,
  Clock,
  Inbox,
  FileText,
  BookOpen,
  Settings,
  CornerDownLeft,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useProjectStore } from '@/store/useProjectStore';
import { useCommandPaletteStore } from '@/store/useCommandPaletteStore';

interface CommandItem {
  id: string;
  group: string;
  label: string;
  sublabel?: string;
  to: string;
  icon: ReactNode;
  /** Extra text folded into the match (ids, codes) but not shown. */
  keywords?: string;
}

// Static navigation + report destinations. Kept in sync with the sidebar/hub.
const GO_TO: Omit<CommandItem, 'group' | 'icon'>[] = [
  { id: 'go-home', label: 'Home', to: '/' },
  { id: 'go-intake', label: 'Intake & Demand', to: '/intake' },
  { id: 'go-portfolios', label: 'Portfolios', to: '/portfolios' },
  { id: 'go-programs', label: 'Programs', to: '/programs' },
  { id: 'go-projects', label: 'Projects', to: '/projects' },
  { id: 'go-schedule', label: 'Schedule', to: '/schedule' },
  { id: 'go-resources', label: 'Capacity & Resources', to: '/resources' },
  { id: 'go-risks', label: 'Risk Register', to: '/risks' },
  { id: 'go-changes', label: 'Change Log', to: '/changes' },
  { id: 'go-documents', label: 'Document Library', to: '/documents' },
  { id: 'go-lessons', label: 'Lessons-Learned', to: '/lessons' },
  { id: 'go-mytasks', label: 'My Tasks', to: '/my-tasks' },
  { id: 'go-timesheet', label: 'My Timesheet', to: '/timesheet' },
  { id: 'go-guide', label: 'User Guide', to: '/guide' },
  { id: 'go-settings', label: 'Settings', to: '/settings' },
];

const REPORTS: Omit<CommandItem, 'group' | 'icon'>[] = [
  { id: 'rep-hub', label: 'Reports', sublabel: 'All reports', to: '/reports' },
  { id: 'rep-briefing', label: 'Executive Briefing', to: '/briefing' },
  { id: 'rep-portfolio-status', label: 'Portfolio Status Report', to: '/reports/portfolio-status' },
  { id: 'rep-benefits', label: 'Benefits Realization Report', to: '/reports/benefits-realization' },
  { id: 'rep-xrisks', label: 'Cross-Project Risk Register', to: '/reports/cross-project-risks' },
  { id: 'rep-changelog', label: 'Change Log Report', to: '/reports/change-log' },
  { id: 'rep-utilization', label: 'Resources Utilization', to: '/reports/resources-utilization' },
  { id: 'rep-capex', label: 'Capital vs Expense', to: '/reports/capex-opex' },
];

const GROUP_ICON: Record<string, ReactNode> = {
  Projects: <FolderKanban className="w-4 h-4" />,
  Portfolios: <Target className="w-4 h-4" />,
  Programs: <Boxes className="w-4 h-4" />,
  Risks: <AlertTriangle className="w-4 h-4" />,
  'Change Requests': <GitBranch className="w-4 h-4" />,
  Resources: <Users className="w-4 h-4" />,
  Reports: <BarChart3 className="w-4 h-4" />,
  'Go to': <ArrowRight className="w-4 h-4" />,
};

// Icon for the static Go-to rows, matched on route so they read like the nav.
const GOTO_ICON: Record<string, ReactNode> = {
  '/': <LayoutDashboard className="w-4 h-4" />,
  '/intake': <Inbox className="w-4 h-4" />,
  '/portfolios': <Target className="w-4 h-4" />,
  '/programs': <Boxes className="w-4 h-4" />,
  '/projects': <FolderKanban className="w-4 h-4" />,
  '/schedule': <Calendar className="w-4 h-4" />,
  '/resources': <Users className="w-4 h-4" />,
  '/risks': <AlertTriangle className="w-4 h-4" />,
  '/changes': <GitBranch className="w-4 h-4" />,
  '/documents': <FileText className="w-4 h-4" />,
  '/lessons': <BookOpen className="w-4 h-4" />,
  '/my-tasks': <Calendar className="w-4 h-4" />,
  '/timesheet': <Clock className="w-4 h-4" />,
  '/guide': <BookOpen className="w-4 h-4" />,
  '/settings': <Settings className="w-4 h-4" />,
};

const GROUP_ORDER = [
  'Projects',
  'Portfolios',
  'Programs',
  'Risks',
  'Change Requests',
  'Resources',
  'Reports',
  'Go to',
];
const PER_GROUP = 6;

export function CommandPalette() {
  const isOpen = useCommandPaletteStore((s) => s.isOpen);
  const close = useCommandPaletteStore((s) => s.close);
  const toggle = useCommandPaletteStore((s) => s.toggle);
  const navigate = useNavigate();

  const projects = useProjectStore((s) => s.projects);
  const portfolios = useProjectStore((s) => s.portfolios);
  const programs = useProjectStore((s) => s.programs);

  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Global shortcut: ⌘K / Ctrl+K toggles the palette from anywhere.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [toggle]);

  // Reset + focus on open.
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setDebounced('');
      setSelected(0);
      const t = setTimeout(() => inputRef.current?.focus(), 20);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  // Debounce the term that actually drives filtering.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim().toLowerCase()), 120);
    return () => clearTimeout(t);
  }, [query]);

  // The full searchable index, rebuilt only when the stores change.
  const index = useMemo<CommandItem[]>(() => {
    const items: CommandItem[] = [];
    for (const p of projects) {
      items.push({ id: `proj-${p.id}`, group: 'Projects', label: p.name, sublabel: p.id, to: `/projects/${p.id}`, icon: GROUP_ICON.Projects, keywords: p.id });
    }
    for (const pf of portfolios) {
      items.push({ id: `pf-${pf.id}`, group: 'Portfolios', label: pf.name, sublabel: pf.id, to: `/portfolios/${pf.id}`, icon: GROUP_ICON.Portfolios, keywords: pf.id });
    }
    for (const pg of programs) {
      items.push({ id: `pg-${pg.id}`, group: 'Programs', label: pg.name, sublabel: pg.id, to: `/programs/${pg.id}`, icon: GROUP_ICON.Programs, keywords: pg.id });
    }
    const resourceNames = new Set<string>();
    for (const p of projects) {
      for (const r of p.risks ?? []) {
        items.push({ id: `risk-${p.id}-${r.id}`, group: 'Risks', label: r.title, sublabel: p.name, to: `/projects/${p.id}?tab=risks`, icon: GROUP_ICON.Risks, keywords: r.id });
      }
      for (const c of p.changes ?? []) {
        items.push({ id: `chg-${p.id}-${c.id}`, group: 'Change Requests', label: c.title, sublabel: p.name, to: `/projects/${p.id}?tab=changes`, icon: GROUP_ICON['Change Requests'], keywords: c.id });
      }
      for (const m of p.team ?? []) {
        if (m.name) resourceNames.add(m.name);
      }
    }
    for (const name of resourceNames) {
      items.push({ id: `res-${name}`, group: 'Resources', label: name, to: '/resources', icon: GROUP_ICON.Resources });
    }
    for (const r of REPORTS) {
      items.push({ ...r, group: 'Reports', icon: GROUP_ICON.Reports });
    }
    for (const g of GO_TO) {
      items.push({ ...g, group: 'Go to', icon: GOTO_ICON[g.to] ?? GROUP_ICON['Go to'] });
    }
    return items;
  }, [projects, portfolios, programs]);

  // Filter + cap per group, then flatten in group order for keyboard nav.
  const { grouped, flat } = useMemo(() => {
    const q = debounced;
    const match = (it: CommandItem) =>
      !q ||
      it.label.toLowerCase().includes(q) ||
      (it.sublabel?.toLowerCase().includes(q) ?? false) ||
      (it.keywords?.toLowerCase().includes(q) ?? false);

    const byGroup = new Map<string, CommandItem[]>();
    for (const it of index) {
      if (!match(it)) continue;
      // With no query, show only quick navigation (Go to) + a few projects so
      // the palette opens useful, not overwhelming.
      if (!q && it.group !== 'Go to' && it.group !== 'Projects') continue;
      const arr = byGroup.get(it.group) ?? [];
      if (arr.length < PER_GROUP) arr.push(it);
      byGroup.set(it.group, arr);
    }
    const grouped = GROUP_ORDER.filter((g) => byGroup.get(g)?.length).map((g) => ({
      group: g,
      items: byGroup.get(g)!,
    }));
    const flat = grouped.flatMap((g) => g.items);
    return { grouped, flat };
  }, [index, debounced]);

  // Keep the selection in range as results change.
  useEffect(() => {
    setSelected((s) => (flat.length === 0 ? 0 : Math.min(s, flat.length - 1)));
  }, [flat.length]);

  const go = (item?: CommandItem) => {
    if (!item) return;
    close();
    navigate(item.to);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((s) => (flat.length ? (s + 1) % flat.length : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((s) => (flat.length ? (s - 1 + flat.length) % flat.length : 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      go(flat[selected]);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close();
    }
  };

  // Scroll the active row into view under keyboard navigation.
  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-cmd-idx="${selected}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  if (!isOpen) return null;

  let runningIdx = -1;

  return (
    <div className="fixed inset-0 z-[300] flex items-start justify-center p-4 pt-[12vh]" role="dialog" aria-modal="true" aria-label="Command palette">
      <div className="absolute inset-0 bg-black/40 animate-fade-in" onClick={close} />
      <div className="relative w-full max-w-xl bg-white rounded-xl border border-line shadow-2xl overflow-hidden animate-fade-in">
        {/* Search input */}
        <div className="flex items-center gap-2 px-4 border-b border-line">
          <Search className="w-4 h-4 text-ink-tertiary shrink-0" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setSelected(0);
            }}
            onKeyDown={onKeyDown}
            placeholder="Search projects, portfolios, risks, reports…"
            className="flex-1 py-3.5 text-sm bg-transparent outline-none text-ink-primary placeholder:text-ink-tertiary"
            aria-label="Search"
          />
          <kbd className="hidden sm:inline text-[10px] font-mono text-ink-tertiary border border-line rounded px-1.5 py-0.5">esc</kbd>
        </div>

        {/* Results */}
        <div ref={listRef} className="max-h-[52vh] overflow-y-auto py-1">
          {flat.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-ink-tertiary">
              No matches for “{query}”.
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.group} className="mb-1">
                <div className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wider text-ink-quaternary flex items-center gap-1.5">
                  <span className="text-ink-tertiary">{GROUP_ICON[g.group]}</span>
                  {g.group}
                </div>
                {g.items.map((it) => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  const active = idx === selected;
                  return (
                    <button
                      key={it.id}
                      type="button"
                      data-cmd-idx={idx}
                      onClick={() => go(it)}
                      onMouseMove={() => setSelected(idx)}
                      className={cn(
                        'w-full flex items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors',
                        active ? 'bg-surface-muted text-ink-primary' : 'text-ink-secondary',
                      )}
                    >
                      <span className={cn('shrink-0', active ? 'text-brand-orange' : 'text-ink-tertiary')}>
                        {it.icon}
                      </span>
                      <span className="flex-1 min-w-0 truncate">{it.label}</span>
                      {it.sublabel && (
                        <span className="shrink-0 text-xs text-ink-tertiary font-mono truncate max-w-[40%]">
                          {it.sublabel}
                        </span>
                      )}
                      {active && <CornerDownLeft className="w-3.5 h-3.5 text-ink-tertiary shrink-0" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        {/* Footer hint */}
        <div className="flex items-center gap-4 px-4 py-2 border-t border-line text-[11px] text-ink-tertiary">
          <span className="flex items-center gap-1"><kbd className="font-mono">↑↓</kbd> navigate</span>
          <span className="flex items-center gap-1"><kbd className="font-mono">↵</kbd> open</span>
          <span className="flex items-center gap-1"><kbd className="font-mono">esc</kbd> close</span>
        </div>
      </div>
    </div>
  );
}
